import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { AbilityService } from 'ability';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

@Injectable()
export class CurrentUserGuard implements CanActivate, CanActivateChild {
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private router = inject(Router);

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.auth.currentUser$.pipe(
      first(),
      map((user) => {
        if (!user) return false;

        if (this.ability.can('read', 'AgentLocal') && !this.ability.can('read', 'AllowSimpleUi')) return true;

        const onboardingPage = 'GettingStart';
        const isCurrentPathExcluded = [RoutingPath.ApHelp].some((path) => state.url.includes(path));

        if (state.url.includes(RoutingPath.ApGettingStartTryRMM)) {
          return true;
        } else if (state.url.includes(onboardingPage)) {
          return user.IsWizardComplete ? this.router.createUrlTree(['AP', 'Administrators']) : true;
        } else if (user.IsWizardComplete || isCurrentPathExcluded) {
          return true;
        } else if (this.router.url.includes(onboardingPage)) {
          return false;
        } else {
          return this.router.createUrlTree(['AP', onboardingPage]);
        }
      })
    );
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return Boolean(this.auth.currentUserValue) || this.auth.fetchCurrentUser$.pipe(map(Boolean));
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return combineLatest([this.auth.loaded$, this.auth.loading$]).pipe(
      first(),
      switchMap(([loaded, loading]) => {
        if (!loading && !loaded) {
          return this.auth.fetchCurrentUser$.pipe(
            map(() => true),
            catchError(() => of(false))
          );
        }
        return of(true);
      })
    );
  }
}
